import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AuthState, StudentInfo } from '../types';
import { authApi } from '../services/authApi';

interface AuthStore extends AuthState {
  login: (email: string, password: string) => Promise<boolean>;
  register: (name: string, email: string, password: string, extraData?: {
    role?: 'student' | 'teacher';
    teacherInfo?: {
      institution: string;
      department: string;
    };
    courseCode?: string;
  }) => Promise<boolean>;
  logout: (notifyServer?: boolean) => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  token: string | null;
  setToken: (token: string | null) => void;
  updateStudentInfo: (studentInfo: StudentInfo) => Promise<boolean>;
  setInfoCompleted: (completed: boolean) => void;
  refreshUser: () => Promise<boolean>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      token: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authApi.login({ email, password });

          if (response.success && response.data) {
            const { token, user } = response.data;

            try { sessionStorage.removeItem('statquest-traffic-session'); } catch {}
            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null
            });
            return true;
          } else {
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
              error: response.message || '登录失败'
            });
            return false;
          }
        } catch (error: any) {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.message || '登录失败，请重试'
          });
          return false;
        }
      },

      register: async (name: string, email: string, password: string, extraData) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authApi.register({
            name,
            email,
            password,
            ...extraData
          });

          if (response.success && response.data) {
            const { token, user } = response.data;

            // For teachers, no token is provided (needs approval)
            if (!token) {
              set({
                user,
                token: null,
                isAuthenticated: false,
                isLoading: false,
                error: null
              });
              return true;
            }

            try { sessionStorage.removeItem('statquest-traffic-session'); } catch {}
            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null
            });
            return true;
          } else {
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
              error: response.message || '注册失败'
            });
            return false;
          }
        } catch (error: any) {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.message || '注册失败，请重试'
          });
          return false;
        }
      },

      logout: async (notifyServer = true) => {
        const token = get().token;
        // Clear immediately even if the best-effort receipt request fails.
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        });
        try { sessionStorage.removeItem('statquest-traffic-session'); } catch {}
        if (token && notifyServer) {
          try {
            await authApi.logout(token);
          } catch {
            // Stateless local logout remains successful.
          }
        }
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setToken: (token: string | null) => {
        set({ token });
      },

      updateStudentInfo: async (studentInfo: StudentInfo) => {
        const token = get().token;
        if (!token) {
          set({ error: '未登录，无法保存信息' });
          return false;
        }

        set({ isLoading: true, error: null });

        try {
          const response = await authApi.updateStudentInfo(token, { studentInfo });

          if (response.success && response.data) {
            const { user } = response.data;
            set({
              user,
              isLoading: false,
              error: null
            });
            return true;
          } else {
            set({
              isLoading: false,
              error: response.message || '保存失败'
            });
            return false;
          }
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || '保存失败，请重试'
          });
          return false;
        }
      },

      setInfoCompleted: (completed: boolean) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: {
              ...currentUser,
              infoCompleted: completed
            }
          });
        }
      },

      refreshUser: async () => {
        const token = get().token;
        if (!token) {
          return false;
        }

        try {
          const response = await authApi.getProfile(token);

          if (response.success && response.data) {
            const { user } = response.data;
            set({
              user,
              isAuthenticated: true,
              error: null
            });
            return true;
          } else {
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              error: response.message || 'Failed to refresh user data'
            });
            return false;
          }
        } catch (error: any) {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            error: error.message || 'Failed to refresh user data'
          });
          return false;
        }
      }
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        token: state.token
      })
    }
  )
);